/**
 * src/components/spatial/SpatialChatPanel.tsx
 *
 * The conversation half of a private interaction.
 *
 * Opening a session already produced a greeting and a message budget on
 * the server; without somewhere to show them, "Open chat" started a real
 * conversation and then discarded both, which looked exactly like a
 * button that does nothing.
 *
 * Private throughout. This session has its own history and prompt mode,
 * and nothing typed here reaches the object's public broadcast — so a
 * rehearsal against a live object cannot be overheard.
 *
 * The message budget is shown rather than merely enforced. Running out
 * mid-sentence with no warning reads as a failure; a visible count reads
 * as a limit.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  endSpatialInteraction,
  sendSpatialMessage,
  type SpatialInteractionSession,
} from '../../services/spatial/spatialPresenceClient';
import ChatMessageBody from './ChatMessageBody';
import { failureText } from './spatialBlockerText';
import {
  applyEnd,
  applySend,
  canSend,
  seedChat,
  type ChatTurn,
} from './spatialChatModel';
import { useUtterancePlayer } from './useUtterancePlayer';

export interface SpatialChatPanelProps {
  session: SpatialInteractionSession;
  objectName: string | null;
  /**
   * Ask the server to synthesise each reply and play it in this tab.
   * Only meaningful for a human participant — an agent reads the text.
   */
  speakReplies?: boolean;
  /** Called once the session is closed, by the operator or by the server. */
  onClosed?: (reason: string) => void;
  /** Mirrors notable moments into the simulator's session log. */
  onLog?: (line: string) => void;
}

const bubbleStyle = (who: ChatTurn['who']): React.CSSProperties => ({
  alignSelf: who === 'you' ? 'flex-end' : 'flex-start',
  maxWidth: '85%',
  padding: '6px 10px',
  borderRadius: 10,
  fontSize: 12,
  lineHeight: 1.45,
  whiteSpace: 'pre-wrap',
  // `pre-wrap` keeps the newlines the answer builders write but will not
  // break a run with no spaces in it, and a contract address is such a run.
  // Without this the panel scrolls sideways whenever a reply names one.
  overflowWrap: 'anywhere',
  minWidth: 0,
  background: who === 'you' ? '#e0e7ff' : '#f1f5f9',
  color: '#0f172a',
});

export const SpatialChatPanel: React.FC<SpatialChatPanelProps> = ({
  session,
  objectName,
  speakReplies = true,
  onClosed,
  onLog,
}) => {
  const [state, setState] = useState(() => seedChat(session));
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const { turns, remaining, closed, error } = state;

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { play } = useUtterancePlayer(onLog);

  // Keep the newest turn in view without yanking the whole overlay.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns]);

  /**
   * Say hello out loud.
   *
   * The greeting is the object's first utterance and is spoken like any
   * other. It arrives with the session rather than being fetched, so
   * this plays what we already hold, once per session — keyed on the
   * session id so a re-render cannot replay it and a second
   * conversation still gets its own hello.
   */
  const greetedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!speakReplies) return;
    if (greetedRef.current === session.sessionId) return;
    const audio = session.greetingSpeech?.audioBase64;
    if (!audio) return;
    greetedRef.current = session.sessionId;
    play(audio, session.greetingSpeech?.mime ?? null);
  }, [session.sessionId, session.greetingSpeech, speakReplies, play]);

  const send = useCallback(async () => {
    const text = draft.trim();
    if (!canSend(state, text, busy)) return;

    setBusy(true);
    const result = await sendSpatialMessage(session.sessionId, text, { speak: speakReplies });
    setBusy(false);

    const next = applySend(state, text, result, new Date().toISOString());
    setState(next);

    if (!result.ok) {
      onLog?.(`chat refused — ${failureText(result)}`);
      // A session the server has forgotten cannot be typed into again.
      if (next.closed) onClosed?.(result.reason);
      return;
    }

    setDraft('');
    if (result.speech?.audioBase64) play(result.speech.audioBase64, result.speech.mime);
  }, [draft, state, busy, session.sessionId, speakReplies, play, onClosed, onLog]);

  const end = useCallback(async () => {
    setBusy(true);
    const result = await endSpatialInteraction(session.sessionId, 'ended_user');
    setBusy(false);
    setState((prev) => applyEnd(prev));
    onLog?.(result.ok ? 'chat ended' : `chat end refused — ${failureText(result)}`);
    onClosed?.('ended_user');
  }, [session.sessionId, onClosed, onLog]);

  return (
    <div className="spatial-admin-row" style={{ display: 'block' }}>
      <label>
        Private conversation with {objectName || 'this object'}
      </label>

      <div
        ref={scrollRef}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          maxHeight: 260,
          overflowY: 'auto',
          // The container half of the overflow fix: the panel never scrolls
          // sideways, whatever a reply contains.
          overflowX: 'hidden',
          padding: 8,
          border: '1px solid #e2e8f0',
          borderRadius: 8,
          background: '#fff',
        }}
      >
        {turns.length === 0 && (
          <div className="spatial-admin-helper" style={{ margin: 0 }}>
            The session opened without a greeting. Say something to start.
          </div>
        )}
        {turns.map((t, i) => (
          <div key={`${t.at}-${i}`} style={bubbleStyle(t.who)}>
            <ChatMessageBody text={t.text} />
          </div>
        ))}
        {busy && (
          <div className="spatial-admin-helper" style={{ margin: 0, alignSelf: 'flex-start' }}>
            …
          </div>
        )}
      </div>

      {!closed ? (
        <>
          <textarea
            className="spatial-admin-input"
            rows={2}
            value={draft}
            disabled={busy || remaining <= 0}
            placeholder={remaining > 0 ? 'Ask the object something…' : 'No messages left in this session.'}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              // Enter sends, shift+enter breaks the line.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
            style={{ marginTop: 8 }}
          />
          <div className="spatial-admin-quickbar">
            <button
              className="spatial-admin-button"
              disabled={!canSend(state, draft, busy)}
              onClick={() => void send()}
            >
              Send
            </button>
            <button className="spatial-admin-button" disabled={busy} onClick={() => void end()}>
              End conversation
            </button>
          </div>
          <div className="spatial-admin-helper">
            {remaining} of the session&apos;s messages left.
            {speakReplies ? ' Replies are spoken in this tab only.' : ''}
          </div>
        </>
      ) : (
        <div className="spatial-admin-helper" style={{ marginTop: 8 }}>
          This conversation is closed. Press Open chat to start another.
        </div>
      )}

      {error && (
        <div className="spatial-admin-helper" style={{ color: '#b91c1c' }}>{error}</div>
      )}
    </div>
  );
};

export default SpatialChatPanel;
